if (!customElements.get('life-slider')) {
  customElements.define(
    'life-slider',
    class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.slides = [...this.track.children];
        this.n = this.slides.length;
        this.current = this.querySelector('[data-current]');
        this.box = this.querySelector('[data-box]');
        this.boxImg = this.querySelector('[data-box-img]');
        this.boxCurrent = this.querySelector('[data-box-current]');
        this.b = 0;
        this.busyUntil = 0;

        // Endless loop: a copy of the photos on each side of the real ones.
        if (this.n > 1) {
          const copy = () =>
            this.slides.map((s) => {
              const c = s.cloneNode(true);
              c.setAttribute('aria-hidden', 'true');
              c.tabIndex = -1;
              c.querySelector('img')?.removeAttribute('loading');
              return c;
            });
          this.track.prepend(...copy());
          this.track.append(...copy());
        }
        this.all = [...this.track.children];
        // Slides without a known shape take it from the photo once it loads.
        this.all.forEach((slide) => {
          if (slide.style.getPropertyValue('--ar')) return;
          const img = slide.querySelector('img');
          if (!img) return;
          const set = () => img.naturalWidth && slide.style.setProperty('--ar', (img.naturalWidth / img.naturalHeight).toFixed(3));
          img.complete ? set() : img.addEventListener('load', () => { set(); if (Date.now() > this.busyUntil) this.jump(this.L); }, { once: true });
        });
        this.L = this.n > 1 ? this.n : 0;

        this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.L - 1));
        this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.L + 1));
        this.all.forEach((slide, k) => {
          slide.addEventListener('click', (e) => {
            if (e.target.closest('.life-slider__caption')) return;
            k === this.L ? this.open(k % this.n) : this.go(k);
          });
          slide.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.open(k % this.n);
            }
          });
        });
        this.track.addEventListener(
          'scroll',
          () => {
            clearTimeout(this.settle);
            this.settle = setTimeout(() => this.sync(), 150);
          },
          { passive: true }
        );
        this.track.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') this.go(this.L - 1);
          if (e.key === 'ArrowRight') this.go(this.L + 1);
        });

        this.querySelector('[data-box-prev]')?.addEventListener('click', () => this.show(this.b - 1));
        this.querySelector('[data-box-next]')?.addEventListener('click', () => this.show(this.b + 1));
        this.querySelector('[data-box-close]')?.addEventListener('click', () => this.box.close());
        this.box?.addEventListener('click', (e) => {
          if (e.target === this.box) this.box.close();
        });
        this.box?.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') this.show(this.b - 1);
          if (e.key === 'ArrowRight') this.show(this.b + 1);
        });
        this.box?.addEventListener('close', () => {
          document.documentElement.style.overflow = '';
          this.jump(this.b + (this.n > 1 ? this.n : 0));
        });
        let x0 = null;
        this.box?.addEventListener('touchstart', (e) => (x0 = e.touches[0].clientX), { passive: true });
        this.box?.addEventListener('touchend', (e) => {
          if (x0 === null) return;
          const dx = e.changedTouches[0].clientX - x0;
          if (Math.abs(dx) > 40) this.show(this.b + (dx < 0 ? 1 : -1));
          x0 = null;
        });

        requestAnimationFrame(() => this.jump(this.L));
        window.addEventListener('load', () => this.jump(this.L), { once: true });
        window.addEventListener('resize', () => this.jump(this.L));
      }

      left(k) {
        const s = this.all[k];
        return s.offsetLeft - (this.track.clientWidth - s.offsetWidth) / 2;
      }

      nearest() {
        const mid = this.track.scrollLeft + this.track.clientWidth / 2;
        let best = 0;
        let dist = Infinity;
        this.all.forEach((s, k) => {
          const d = Math.abs(s.offsetLeft + s.offsetWidth / 2 - mid);
          if (d < dist) {
            dist = d;
            best = k;
          }
        });
        return best;
      }

      // After scrolling stops, move from a copy back to the real photo
      // (same picture, so nothing visibly changes).
      sync() {
        const wait = this.busyUntil - Date.now();
        if (wait > 0) {
          clearTimeout(this.settle);
          this.settle = setTimeout(() => this.sync(), wait + 50);
          return;
        }
        this.L = this.nearest();
        if (this.n > 1 && (this.L < this.n || this.L >= 2 * this.n)) {
          this.jump(this.n + (this.L % this.n));
        } else {
          this.mark();
        }
      }

      mark() {
        const real = this.L % this.n;
        this.all.forEach((s, k) => s.classList.toggle('is-current', k % this.n === real));
        if (this.current) this.current.textContent = real + 1;
      }

      jump(k) {
        this.L = k;
        this.track.style.scrollSnapType = 'none';
        this.track.scrollLeft = this.left(k);
        this.track.offsetWidth;
        this.track.style.scrollSnapType = '';
        this.mark();
      }

      go(k) {
        if (this.n > 1 && (k < 1 || k > 3 * this.n - 2)) {
          const shift = k < this.n ? this.n : -this.n;
          this.jump(this.L + shift);
          k += shift;
        }
        this.L = k;
        this.busyUntil = Date.now() + 700;
        this.mark();
        this.track.scrollTo({ left: this.left(k), behavior: 'smooth' });
      }

      open(n) {
        if (!this.box) return;
        this.show(n);
        document.documentElement.style.overflow = 'hidden';
        this.box.showModal();
      }

      show(i) {
        this.b = (i + this.n) % this.n;
        const s = this.slides[this.b];
        this.boxImg.src = s.dataset.full;
        this.boxImg.alt = s.dataset.alt || '';
        if (this.boxCurrent) this.boxCurrent.textContent = this.b + 1;
        const next = this.slides[(this.b + 1) % this.n];
        if (next) new Image().src = next.dataset.full;
      }
    }
  );
}
